import { useEffect, useId, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from "react";
import { fehlertext } from "../fehler";
import { useHinweis } from "./Hinweise";
import { Schwebend } from "./Schwebend";
import s from "./statuswahl.module.css";

/* Statuswahl (BAUPLAN-STATUSWAHL.md, Abschnitt 2, Mockup docs/app/mockups/Statuswahl-2026-10-16.html). Eine Pille mit Zeichen oder drei
   Balken, Wort und Pfeil. Sie öffnet ein schwebendes Fenster (am Handy unten) mit einer Liste der Optionen, je mit kurzer Erklärung.
   Status nie nur als Farbe: Das Wort steht immer dabei, dazu Zeichen oder Balken. Die Optionen je Stelle liegen in den Modulen.

   Dieselbe Pille wählt auch Werte wie Rolle, Priorität oder Sichtbarkeit (Hülle Wertwahl, BAUPLAN-FEINSCHLIFF.md, Abschnitt 3.2):
   `art` ersetzt „Status“ im Fenster und im Namen für Screenreader, der Ton `weiss` ist die Pille ohne Farbe. In Listen hat die Pille
   eine feste Breite (`breite`), damit Zeichen, Wort und Pfeil untereinander stehen.

   In Aufgabenlisten steht der Status als Zeichen vorne (`alsZeichen`, BAUPLAN-STATUS-IN-LISTEN.md, ADR-023): nur das Zeichen, 22 px in
   44 px, in der Farbe des Tons. Menü, Tastatur und Name für Screenreader sind dieselben wie bei der Pille. */

/** `klar` ohne Fläche (Priorität in Listen), `leise` grau ohne Fläche („Kein Vorhaben“, „Ohne Ressort“), `r0` bis `r6` die Farben der
 *  Ressorts, `wartet` für „Wartet“ in Aufgabenlisten (--wartet-ink) */
export type StatusTon = "leer" | "neutral" | "warten" | "wartet" | "grau" | "ok" | "aus" | "stark" | "weiss" | "hoch" | "klar" | "leise" | RessortTon;
export type RessortTon = "r0" | "r1" | "r2" | "r3" | "r4" | "r5" | "r6";
export const RESSORT_TOENE: readonly RessortTon[] = ["r0", "r1", "r2", "r3", "r4", "r5", "r6"];
/** Kleine Symbole links in der Pille. Namen statt Elementen, damit die Logik der Module ohne React bleibt. */
export type StatusZeichen = "kreis" | "halbkreis" | "uhr" | "erledigt" | "gestrichelt" | "flieger" | "blatt" | "haken" | "kreuz" | "fahne" | "fahneHoch" | "fahneLeer";

export interface StatusOption<T extends string> {
  wert: T;
  /** „Zugesagt“ */
  text: string;
  /** „Act hat zugesagt, Vertrag fehlt“ */
  erklaerung: string;
  ton: StatusTon;
  zeichen?: StatusZeichen | undefined;
  /** 0 bis 3, nur Line-up: Fortschritt als drei Balken */
  stufe?: number | undefined;
  /** Farbpunkt vor dem Wort (Vorhaben in Aufgabenlisten), eine CSS-Farbe. Beiwerk, das Wort trägt die Bedeutung. */
  punkt?: string | undefined;
}

const STRICH = { fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" } as const;
const svg = (inhalt: ReactNode, groesse = 16) => (
  <svg viewBox="0 0 24 24" width={groesse} height={groesse} {...STRICH} aria-hidden="true">
    {inhalt}
  </svg>
);
const ZEICHEN: Record<StatusZeichen, ReactNode> = {
  kreis: svg(<circle cx="12" cy="12" r="8" />),
  halbkreis: svg(
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 4a8 8 0 0 1 0 16z" fill="currentColor" />
    </>,
  ),
  uhr: svg(
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 8v4l3 2" />
    </>,
  ),
  erledigt: svg(
    <>
      <circle cx="12" cy="12" r="8" fill="currentColor" />
      <path d="M8.5 12l2.5 2.5L15.5 10" stroke="#fff" />
    </>,
  ),
  gestrichelt: svg(<circle cx="12" cy="12" r="8" strokeDasharray="3 3" />),
  flieger: svg(<path d="M4 12l16-7-6 16-3-6z" />),
  blatt: svg(
    <>
      <path d="M7 3h7l4 4v14H7z" />
      <path d="M14 3v4h4" />
    </>,
  ),
  haken: svg(
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="M8.5 12l2.5 2.5L15.5 10" />
    </>,
  ),
  // Abgelehnt (Stand eines Förderantrags, BAUPLAN-VORHABEN-ARTEN.md, Abschnitt 1)
  kreuz: svg(
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="M9.5 9.5l5 5M14.5 9.5l-5 5" />
    </>,
  ),
  // Priorität wie bisher im Dialog: Fahne voll (Hoch, Normal) oder leer (Niedrig)
  fahne: svg(<path d="M5 21V4h12l-2.5 4.5L17 13H5" fill="currentColor" />),
  // Hoch in Aufgabenlisten (BAUPLAN-AUFGABENLISTE.md, Abschnitt 3): Fahne in --danger, das Wort bleibt dunkel
  fahneHoch: svg(<path d="M5 21V4h12l-2.5 4.5L17 13H5" fill="var(--danger)" stroke="var(--danger)" />),
  fahneLeer: svg(<path d="M5 21V4h12l-2.5 4.5L17 13H5" />),
};

const PFEIL = (
  <svg className={s.pfeil} viewBox="0 0 10 6" width="10" height="6" aria-hidden="true">
    <path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);
const HAKEN = svg(<path d="M5 12l5 5L20 7" />, 18);
const WEITER = svg(<path d="M5 12h14M13 6l6 6-6 6" />);

/** Pille nur zur Anzeige, ohne Menü und ohne den gestrichelten Rand für „gesperrt“: zum Beispiel der Stand eines Förderantrags in einer
 *  Kachel, die ganz ein Link ist (BAUPLAN-VORHABEN-ARTEN.md, Abschnitt 7.1). Zeichen und Wort wie in der Statuswahl. */
export function StatusPille<T extends string>({ option, art = "Status", klein = false }: { option: StatusOption<T>; art?: string; klein?: boolean }) {
  return (
    <span className={[s.pille, s.anzeige, s[`t_${option.ton}`], klein && s.klein].filter(Boolean).join(" ")}>
      <StatusSymbol o={option} />
      <span className={s.wort}>
        <span className="nur-leser">{art}: </span>
        {option.text}
      </span>
    </span>
  );
}

/** Zeichen oder die drei Balken einer Option. Stufe 0 alle blass, Stufe 3 alle in Orange. */
function StatusSymbol({ o }: { o: Pick<StatusOption<string>, "zeichen" | "stufe" | "punkt"> }) {
  if (o.punkt) return <span className={s.punkt} style={{ background: o.punkt }} aria-hidden="true" />;
  if (o.stufe !== undefined) {
    const stufe = o.stufe;
    return (
      <span className={s.stufen} aria-hidden="true">
        {[1, 2, 3].map((i) => (
          <span key={i} className={i <= stufe ? (stufe >= 3 ? s.ziel : s.an) : undefined} />
        ))}
      </span>
    );
  }
  return o.zeichen ? (
    <span className={s.zeichen} aria-hidden="true">
      {ZEICHEN[o.zeichen]}
    </span>
  ) : null;
}

interface Props<T extends string> {
  /** Wessen Status, für „Status von …“. Leer: nur `art` (Filter). */
  name: string;
  /** Was gewählt wird, Vorgabe „Status“: „Rolle von Lena Brandt“ im Fenster und im Namen für Screenreader */
  art?: string | undefined;
  optionen: readonly StatusOption<T>[];
  wert: T;
  /** Ohne Recht: Pille ohne Pfeil, nicht klickbar */
  darf?: boolean | undefined;
  /** Während des Speicherns gesperrt */
  laeuft?: boolean | undefined;
  /** Knopf „Weiter zu …“ für die nächste Option */
  weiter?: boolean | undefined;
  /** Für Tabellenzellen */
  klein?: boolean | undefined;
  /** Feste Breite in px für Spalten einer Liste (CSS-Variable --spalte-pille): Inhalt linksbündig, Pfeil rechts außen */
  breite?: number | undefined;
  /** Füllt die Zelle (Aufgabenliste, BAUPLAN-AUFGABENLISTE-BREITEN.md): Wort in einer Zeile, sonst „…“, der volle Wert im title */
  fuellen?: boolean | undefined;
  /** Anderes Wort und anderer Ton in der Pille als die gewählte Option, für Filter („Ressort“ weiß, „Technik“ schwarz) */
  anzeige?: { text: string; ton: StatusTon } | undefined;
  /** Leises Wort vor dem Wert in der Pille, zum Beispiel „Gruppieren:“ in Werkzeugleisten (BAUPLAN-SPRINT-KOPF.md, Abschnitt 6) */
  vorsatz?: string | undefined;
  /** Nur das Zeichen statt der Pille (Aufgabenlisten): 22 px in 44 px, Tooltip „Status: In Arbeit“ */
  alsZeichen?: boolean | undefined;
  /** Mit `alsZeichen` und ohne Recht: warum es nicht geht. Das Zeichen bleibt fokussierbar, liest den Grund vor und zeigt ihn beim
   *  Klick als Hinweis, wie der Haken. Ohne Grund ist das Zeichen nur Anzeige (Archiv). */
  gesperrt?: string | null | undefined;
  onWechsel?: ((neu: T, alt: T) => void) | undefined;
}

export function Statuswahl<T extends string>({
  name,
  optionen,
  wert,
  darf = false,
  laeuft = false,
  weiter = false,
  klein = false,
  art = "Status",
  breite,
  fuellen = false,
  anzeige,
  vorsatz,
  alsZeichen = false,
  gesperrt,
  onWechsel,
}: Props<T>) {
  const id = useId();
  const knopf = useRef<HTMLButtonElement>(null);
  const liste = useRef<HTMLDivElement>(null);
  const [offen, setOffen] = useState(false);
  const [aktiv, setAktiv] = useState(0);
  const index = optionen.findIndex((x) => x.wert === wert);
  const o: StatusOption<T> = optionen[index] ?? { wert, text: wert, erklaerung: "", ton: "neutral" };
  const naechste = weiter && index >= 0 ? optionen[index + 1] : undefined;
  const titel = name ? `${art} von ${name}` : art;
  const ton = anzeige?.ton ?? o.ton;
  const pille = [s.pille, s[`t_${ton}`], klein && s.klein, breite && !fuellen && s.spalte, fuellen && s.fuellen, !darf && s.gesperrt].filter(Boolean).join(" ");
  const stil = breite && !fuellen ? ({ "--spalte-pille": `${breite}px` } as CSSProperties) : undefined;
  // Werte ohne Zeichen (Rolle, Sichtbarkeit): Liste ohne Farbfeld links
  const mitSymbol = optionen.some((x) => x.zeichen || x.punkt || x.stufe !== undefined);
  const symbol = anzeige ? null : <StatusSymbol o={o} />;
  const wort = anzeige?.text ?? o.text;
  // Gekürzte Pille: der volle Wert beim Überfahren. Als Zeichen: „Status: In Arbeit“.
  const voll = alsZeichen ? `${art}: ${o.text}` : fuellen ? wort : undefined;
  const zeichenKlasse = [s.alsZeichen, s[`zf_${ton}`]].filter(Boolean).join(" ");

  // Beim Öffnen steht die Markierung auf dem aktuellen Wert, der Fokus in der Liste
  useEffect(() => {
    if (offen) liste.current?.focus({ preventScroll: true });
  }, [offen]);

  if (!darf && alsZeichen) {
    return gesperrt ? (
      <ZeichenGesperrt klasse={zeichenKlasse} titel={`${titel}: ${o.text}`} tooltip={`${voll}. ${gesperrt}`} grund={gesperrt}>
        {symbol}
      </ZeichenGesperrt>
    ) : (
      <span className={zeichenKlasse} title={voll}>
        {symbol}
        <span className="nur-leser">{voll}</span>
      </span>
    );
  }

  if (!darf) {
    return (
      <span className={pille} style={stil} title={voll}>
        {symbol}
        <span className={s.wort}>
          <span className="nur-leser">{art}: </span>
          {wort}
        </span>
      </span>
    );
  }

  function oeffnen() {
    setAktiv(Math.max(0, index));
    setOffen(true);
  }

  function schliessen() {
    setOffen(false);
    knopf.current?.focus();
  }

  function waehlen(neu: T) {
    schliessen();
    if (neu !== wert) onWechsel?.(neu, wert);
  }

  function taste(e: KeyboardEvent<HTMLDivElement>) {
    const n = optionen.length;
    const ziel = { ArrowDown: aktiv + 1, ArrowUp: aktiv - 1, Home: 0, End: n - 1 }[e.key];
    if (ziel !== undefined) {
      e.preventDefault();
      setAktiv((ziel + n) % n);
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      const x = optionen[aktiv];
      if (x) waehlen(x.wert);
    } else if (e.key === "Tab") {
      // Escape schließt Schwebend selbst und gibt den Fokus zurück
      e.preventDefault();
      schliessen();
    }
  }

  return (
    <>
      <button
        ref={knopf}
        type="button"
        className={alsZeichen ? zeichenKlasse : pille}
        style={alsZeichen ? undefined : stil}
        title={voll}
        aria-haspopup="listbox"
        aria-expanded={offen}
        aria-disabled={laeuft || undefined}
        aria-label={`${titel}: ${o.text}, ändern`}
        onClick={() => {
          if (offen) setOffen(false);
          else if (!laeuft) oeffnen();
        }}
        onKeyDown={(e) => {
          if ((e.key === "ArrowDown" || e.key === "ArrowUp") && !offen && !laeuft) {
            e.preventDefault();
            oeffnen();
          }
        }}
      >
        {symbol}
        {alsZeichen ? null : (
          <>
            {vorsatz ? (
              <span className={s.vorsatz} aria-hidden="true">
                {vorsatz}
              </span>
            ) : null}
            <span className={s.wort}>{wort}</span>
            {PFEIL}
          </>
        )}
      </button>
      <Schwebend anker={knopf} offen={offen} onSchliessen={() => setOffen(false)} label={titel} breite={300} ausrichtung="auto">
        <div className={s.kopf} id={`${id}-kopf`}>
          {name ? (
            <>
              {art} von <b>{name}</b>
            </>
          ) : (
            <b>{art}</b>
          )}
        </div>
        <div
          ref={liste}
          className={[s.liste, !mitSymbol && s.ohneSymbol].filter(Boolean).join(" ")}
          role="listbox"
          tabIndex={0}
          aria-labelledby={`${id}-kopf`}
          aria-activedescendant={`${id}-${aktiv}`}
          onKeyDown={taste}
        >
          {optionen.map((x, i) => (
            <div
              key={x.wert}
              id={`${id}-${i}`}
              role="option"
              aria-selected={x.wert === wert}
              className={[s.option, i === aktiv && s.aktiv].filter(Boolean).join(" ")}
              onClick={() => waehlen(x.wert)}
              onPointerMove={() => i !== aktiv && setAktiv(i)}
            >
              {mitSymbol ? (
                <span className={[s.feld, s[`t_${x.ton}`]].filter(Boolean).join(" ")}>
                  <StatusSymbol o={x} />
                </span>
              ) : null}
              <span className={s.optionText}>
                <b>{x.text}</b>
                {x.erklaerung ? <small>{x.erklaerung}</small> : null}
              </span>
              <span className={s.haken}>{x.wert === wert ? HAKEN : null}</span>
            </div>
          ))}
        </div>
        {naechste ? (
          <div className={s.fuss}>
            <button type="button" className={s.weiter} onClick={() => waehlen(naechste.wert)}>
              Weiter zu „{naechste.text}“{WEITER}
            </button>
          </div>
        ) : null}
      </Schwebend>
    </>
  );
}

/** Zeichen ohne Recht: fokussierbar, nennt den Grund für Screenreader und zeigt ihn beim Klick als Hinweis (wie der Haken) */
function ZeichenGesperrt({ klasse, titel, tooltip, grund, children }: { klasse: string; titel: string; tooltip: string; grund: string; children: ReactNode }) {
  const { zeigen } = useHinweis();
  const grundId = useId();
  return (
    <button type="button" className={klasse} aria-label={titel} aria-disabled="true" aria-describedby={grundId} title={tooltip} onClick={() => zeigen(grund)}>
      {children}
      <span id={grundId} className="nur-leser">
        {grund}
      </span>
    </button>
  );
}

/** Speichern nach dem Wählen (Abschnitt 2.3): Hinweis „Name: Neuer Status“ mit „Rückgängig“, das den alten Wert zurücksetzt. Scheitert
 *  das Speichern, kommt ein Fehlerhinweis. Den angezeigten Wert setzt die Datenschicht zurück (optimistisch mit Rücksprung). */
export function useStatusSpeichern() {
  const { zeigen } = useHinweis();
  return function speichernMitHinweis<T extends string>(
    name: string,
    optionen: readonly StatusOption<T>[],
    neu: T,
    alt: T,
    speichern: (wert: T) => Promise<unknown>,
  ) {
    const fehler = (e: unknown) => zeigen(fehlertext(e), { fehler: true });
    const text = optionen.find((x) => x.wert === neu)?.text ?? neu;
    speichern(neu).then(() => zeigen(`${name}: ${text}`, { rueckgaengig: () => void speichern(alt).catch(fehler) }), fehler);
  };
}
